import { AppSettingsContentResponseModel } from '@/services/platform/api/model/appSettingsContentResponseModel';

export enum PageMode {
  Default = 'default',
  BookingDetail = 'booking-detail',
  BookingEngine = 'booking-engine',
  ActivityDetail = 'activity-detail',
  VenueDetail = 'venue-detail',
  EventDetail = 'event-detail',
  NotificationsInbox = 'notifications-inbox',
  Shop = 'shop',
  ShopCategory = 'shop-category',
  ShopCollection = 'shop-collection',
  ShopSubcategory = 'shop-subcategory',
  ShopBrands = 'shop-brands',
  ShopBrand = 'shop-brand',
  ShopProduct = 'shop-product',
  ShopBag = 'shop-bag',
  ShopDelivery = 'shop-delivery',
  ShopAddAddress = 'shop-add-address',
  ShopCheckout = 'shop-checkout',
}

export interface ShopUrlSegments {
  categoryId?: string;
  subcategoryId?: string;
  collectionId?: string;
  sectionPath?: string;
  productId?: string;
  brandId?: string;
}

/**
 * Scans all segments of a pathname for shop entity ID patterns and returns
 * whichever are present. Checks in specificity order (subcategory before
 * category) so `subcategory_xxx` is never mis-matched as `category_xxx`.
 *
 * @param pathname - The full URL pathname, e.g. `/en/shop/women/category_abc/subcategory_def/product_xyz`.
 * @returns An object with whichever of `categoryId`, `subcategoryId`,
 *   `collectionId` could be extracted from the path, plus `sectionPath` — the
 *   `collectionId`, and `productId` could be extracted from the path.
 *   relative URL formed by the pathname's first 3 segments (e.g. `/en/shop/women`).
 */
/**
 * The id from a `<prefix>_<id>` segment, or `undefined` when there isn't a usable one.
 *
 * A template literal turns a missing id into the *string* `"undefined"`, so a link built from one
 * arrives here as a segment like `collection_undefined` — and `"undefined"` is truthy, so it sails
 * past every `if (id)` guard downstream and gets sent to the CMS as a real id, which 404s. Treating
 * those spellings as absent means a link that was generated wrong (or a URL someone bookmarked or
 * shared before it was fixed) degrades to "no filter" instead of a failed request.
 */
function segmentId(segment: string, prefix: string): string | undefined {
  const id = segment.slice(prefix.length);
  return id && id !== 'undefined' && id !== 'null' ? id : undefined;
}

export function parseShopUrlSegments(pathname: string): ShopUrlSegments {
  const segments = pathname.split('/').filter(Boolean);
  const result: ShopUrlSegments = { sectionPath: `/${segments.slice(0, 3).join('/')}` };
  for (const segment of segments) {
    if (segment.startsWith('subcategory_')) {
      result.subcategoryId = segmentId(segment, 'subcategory_');
    } else if (segment.startsWith('category_')) {
      result.categoryId = segmentId(segment, 'category_');
    } else if (segment.startsWith('collection_')) {
      result.collectionId = segmentId(segment, 'collection_');
    } else if (segment.startsWith('product_')) {
      result.productId = segmentId(segment, 'product_');
    } else if (segment.startsWith('brand_')) {
      result.brandId = segmentId(segment, 'brand_');
    }
  }
  return result;
}

/**
 * Strips leading and trailing slashes so a CMS route path and a router pathname can be compared.
 *
 * Umbraco publishes route paths with a trailing slash (`/en/shop/checkout/`) while the pathname the
 * router hands over has none, so comparing them raw makes an exact-match check fail on live data and
 * pass in tests — the fixtures are authored without it.
 */
function trimSlashes(value: string | undefined): string {
  return value ? value.replace(/^\/+|\/+$/g, '') : '';
}

export function getPageMode(
  appSettings: AppSettingsContentResponseModel | undefined,
  fullPath: string | undefined
): PageMode {
  if (!appSettings || !fullPath) return PageMode.Default;

  const bookingDetailUrl = appSettings.properties?.bookingDetail?.route?.path;
  const bookingEngineUrl = appSettings.properties?.bookingIntro?.route?.path;
  const activityDetailUrl = appSettings.properties?.activityDetail?.route?.path;
  const eventDetailUrl = appSettings.properties?.eventDetail?.route?.path;
  const shopUrl = appSettings.properties?.shop?.route?.path;
  const shopShoppingBagUrl = appSettings.properties?.shopShoppingBag?.route?.path;
  const shopCheckoutUrl = appSettings.properties?.shopCheckout?.route?.path;
  const prefixedPath = `/${fullPath}`;

  if (bookingDetailUrl && prefixedPath.includes(bookingDetailUrl)) return PageMode.BookingDetail;
  if (bookingEngineUrl && prefixedPath.includes(bookingEngineUrl)) return PageMode.BookingEngine;
  if (activityDetailUrl && prefixedPath.includes(activityDetailUrl)) return PageMode.ActivityDetail;
  if (prefixedPath.includes('/venue-detail/')) return PageMode.VenueDetail;
  if (prefixedPath.includes('/notifications/inbox')) return PageMode.NotificationsInbox;
  if (eventDetailUrl && prefixedPath.includes(eventDetailUrl)) return PageMode.EventDetail;

  if (shopUrl && prefixedPath.includes(shopUrl)) {
    const segments = fullPath.split('/');
    const lastSegment = segments[segments.length - 1] ?? '';

    /*
     * The whole checkout subtree, classified off the `shopCheckout` mapping alone.
     *
     * `delivery` and `address/new` have no CMS node of their own — they are client-side segments
     * appended to whatever URL `shopCheckout` resolves to. They used to be found with
     * `segments.includes('checkout')`, which meant renaming the node in Umbraco left them falling
     * through to the payment screen instead of the delivery or address step: a half-broken flow
     * rather than a clean failure. Deriving the base from the mapping makes the rename a non-event.
     *
     * Prefix comparison rather than `includes` for the same class of reason — a sibling page whose
     * path merely *contained* the checkout URL was being classified as checkout.
     *
     * With the mapping unset nothing here matches and the path falls through to `PageMode.Shop`.
     * That is deliberate: the flow cannot work without the mapping, and the navigations into it
     * (`ShopBagScreen`, `ShopDeliveryScreen`) now refuse to move rather than guess a URL.
     */
    const checkoutBase = trimSlashes(shopCheckoutUrl);
    if (checkoutBase) {
      const path = trimSlashes(prefixedPath);
      if (path === checkoutBase) return PageMode.ShopCheckout;
      if (path.startsWith(`${checkoutBase}/`)) {
        const rest = path.slice(checkoutBase.length + 1);
        if (rest === 'delivery') return PageMode.ShopDelivery;
        if (rest === 'address' || rest.startsWith('address/')) return PageMode.ShopAddAddress;
        return PageMode.ShopCheckout;
      }
    }

    if (lastSegment.includes('product_')) return PageMode.ShopProduct;
    if (lastSegment.includes('subcategory_')) return PageMode.ShopSubcategory;
    if (lastSegment.includes('category_')) return PageMode.ShopCategory;
    if (lastSegment.includes('collection_')) return PageMode.ShopCollection;
    if (lastSegment.includes('brands_')) return PageMode.ShopBrands;
    if (lastSegment.includes('brand_')) return PageMode.ShopBrand;
    if (shopShoppingBagUrl && prefixedPath.includes(shopShoppingBagUrl)) return PageMode.ShopBag;
    return PageMode.Shop;
  }

  return PageMode.Default;
}
